import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle, AlertTriangle, XCircle, Users, Loader2, Trash2, RefreshCw, ShieldCheck, Copy, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const NONE = '__none__';

const STATUS = {
  ready: { label: 'Ready', cls: 'bg-green-100 text-green-800', icon: CheckCircle },
  warning: { label: 'Check', cls: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  update: { label: 'Update existing', cls: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  duplicate: { label: 'Already a member', cls: 'bg-slate-100 text-slate-700', icon: Users },
  error: { label: 'Error', cls: 'bg-red-100 text-red-800', icon: XCircle },
};

const Steps = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs sm:text-sm flex-wrap" data-testid="import-steps">
    {['Upload file', 'Match columns', 'Review rows', 'Import'].map((s, i) => (
      <li key={s} className="flex items-center gap-2">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${i + 1 <= step ? 'bg-[hsl(var(--gs-navy))] text-white' : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'}`}>{i + 1}</span>
        <span className={i + 1 === step ? 'font-semibold' : 'text-[hsl(var(--muted-foreground))]'}>{s}</span>
        {i < 3 && <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />}
      </li>
    ))}
  </ol>
);

export function MemberImportTab({ token }) {
  const [batches, setBatches] = useState(null);
  const [fields, setFields] = useState([]);
  const [batch, setBatch] = useState(null);       // full batch with results
  const [mapping, setMapping] = useState({});
  const [options, setOptions] = useState({ expiry_rule: 'file_or_1y', number_rule: 'keep_valid', duplicate_rule: 'skip' });
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTick, setConfirmTick] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try { setBatches((await api.getAuth('/api/admin/member-import/batches', token)).batches || []); } catch (e) { toast.error(e.message); setBatches([]); }
  }, [token]);
  useEffect(() => { loadBatches(); api.getAuth('/api/admin/member-import/fields', token).then(d => setFields(d.fields || [])).catch(() => {}); }, [loadBatches, token]);

  const openBatch = async (id) => {
    try {
      const d = await api.getAuth(`/api/admin/member-import/batches/${id}`, token);
      setBatch(d.batch); setMapping(d.batch.mapping || {}); setOptions({ expiry_rule: 'file_or_1y', number_rule: 'keep_valid', duplicate_rule: 'skip', ...(d.batch.options || {}) });
      setSelected(new Set((d.batch.results || []).filter(r => r.selected).map(r => r.row)));
      setResult(d.batch.status === 'committed' ? d.batch.committed : null);
    } catch (e) { toast.error(e.message); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/member-import/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || 'Upload failed');
      toast.success(`${j.batch.row_count} rows staged — nothing has been imported yet`);
      if (j.truncated) toast.warning('Only the first 5000 rows were read');
      await loadBatches(); await openBatch(j.batch.batch_id);
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  const validate = async () => {
    setValidating(true);
    try {
      const d = await api.putAuth(`/api/admin/member-import/batches/${batch.batch_id}/mapping`, { mapping, options }, token);
      setBatch({ ...batch, ...d.batch, results: d.results, summary: d.summary });
      setSelected(new Set(d.results.filter(r => r.selected).map(r => r.row)));
      toast.success(`Checked ${d.summary.total} rows`);
    } catch (e) { toast.error(e.message); }
    setValidating(false);
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const d = await api.postAuth(`/api/admin/member-import/batches/${batch.batch_id}/commit`, { confirm: true, selected_rows: Array.from(selected) }, token);
      setResult({ ...d, created: d.created.length, updated: d.updated.length, skipped: d.skipped.length, created_list: d.created });
      setBatch({ ...batch, status: 'committed' }); setConfirmOpen(false); setConfirmTick(false);
      toast.success(`Imported ${d.created.length} new member${d.created.length === 1 ? '' : 's'}${d.updated.length ? `, updated ${d.updated.length}` : ''}. No emails were sent.`);
      loadBatches();
    } catch (e) { toast.error(e.message); }
    setCommitting(false);
  };

  const discard = async () => {
    if (!window.confirm(batch.status === 'committed' ? 'Remove the raw file data from this import record?' : 'Discard this upload? Nothing has been imported.')) return;
    try { const d = await api.deleteAuth(`/api/admin/member-import/batches/${batch.batch_id}`, token); toast.success(d.message); setBatch(null); loadBatches(); } catch (e) { toast.error(e.message); }
  };

  const results = batch?.results || [];
  const shown = results.filter(r => filter === 'all' || r.status === filter);
  const step = !batch ? 1 : batch.status === 'committed' ? 4 : results.length ? 3 : 2;
  const importable = results.filter(r => selected.has(r.row) && ['ready', 'warning', 'update'].includes(r.status));

  return (
    <div className="space-y-6" data-testid="member-import-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Import Existing Members</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Upload your CSV or Excel list, match the columns, review every row, then import. Nothing reaches the member database until you confirm — and <strong>no emails are sent</strong> by the import.</p>
        </div>
        {batch && <Button variant="outline" size="sm" onClick={() => { setBatch(null); setResult(null); }} data-testid="import-back-to-list">← All uploads</Button>}
      </div>
      <Steps step={step} />

      {!batch && (
        <>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Upload className="w-5 h-5" /> Upload a member list</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.txt" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[hsl(var(--gs-navy))]" data-testid="import-upload-button">{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading file…</> : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Choose CSV or Excel file</>}</Button>
              <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                <p>First row must contain column headings (e.g. Name, Email, DOB, Postcode, Membership No, Expiry). Columns are matched automatically and you can correct them.</p>
                <p>Rows are checked for missing names, bad emails/dates, duplicates within the file and people who are already members.</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Previous uploads</CardTitle></CardHeader>
            <CardContent>
              {batches === null ? <Skeleton className="h-16" /> : batches.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="import-no-batches">No uploads yet.</p> : (
                <div className="divide-y">
                  {batches.map(b => (
                    <button type="button" key={b.batch_id} onClick={() => openBatch(b.batch_id)} className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-[hsl(var(--muted)/0.4)] px-2 rounded" data-testid={`import-batch-${b.batch_id}`}>
                      <div className="min-w-0"><div className="font-medium truncate">{b.filename}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(b.created_at).toLocaleString('en-GB')} · {b.row_count} rows · {b.uploaded_by}</div></div>
                      <Badge className={b.status === 'committed' ? 'bg-green-100 text-green-800' : b.status === 'validated' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{b.status === 'committed' ? `Imported (${b.committed?.created ?? 0} new)` : b.status === 'validated' ? 'Reviewed — not imported' : 'Uploaded — not imported'}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {batch && batch.status !== 'committed' && (
        <Card data-testid="import-mapping-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2">2. Match columns <Badge variant="outline">{batch.filename} · {batch.row_count} rows</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {fields.map(f => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}{f.required && ' *'}</Label>
                  <Select value={mapping[f.key] || NONE} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === NONE ? undefined : v })}>
                    <SelectTrigger className="h-9" data-testid={`import-map-${f.key}`}><SelectValue placeholder="Not in file" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Not in file —</SelectItem>
                      {(batch.headers || []).map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {batch.sample_rows?.length > 0 && (
              <details className="text-xs"><summary className="cursor-pointer text-[hsl(var(--muted-foreground))]">Preview first rows of the file</summary>
                <div className="overflow-x-auto mt-2"><Table><TableHeader><TableRow>{batch.headers.map(h => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>{batch.sample_rows.map((r, i) => <TableRow key={stableRecordKey(r)}>{batch.headers.map(h => <TableCell key={h} className="text-xs whitespace-nowrap max-w-[200px] truncate">{r[h]}</TableCell>)}</TableRow>)}</TableBody></Table></div>
              </details>
            )}
            <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t">
              <div><Label className="text-xs">Membership expiry</Label>
                <Select value={options.expiry_rule} onValueChange={v => setOptions({ ...options, expiry_rule: v })}><SelectTrigger className="h-9" data-testid="import-opt-expiry"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="file_or_1y">Use expiry column, else 1 year from today</SelectItem><SelectItem value="file_or_2y">Use expiry column, else 2 years from today</SelectItem><SelectItem value="1y">Always 1 year from today</SelectItem><SelectItem value="2y">Always 2 years from today</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Membership numbers</Label>
                <Select value={options.number_rule} onValueChange={v => setOptions({ ...options, number_rule: v })}><SelectTrigger className="h-9" data-testid="import-opt-number"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="keep_valid">Keep if already in new 18-character format, else issue new</SelectItem><SelectItem value="generate">Always issue a new number (old one kept as reference)</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">If the person is already a member</Label>
                <Select value={options.duplicate_rule} onValueChange={v => setOptions({ ...options, duplicate_rule: v })}><SelectTrigger className="h-9" data-testid="import-opt-duplicate"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="skip">Skip the row (safe)</SelectItem><SelectItem value="update">Update their details from the file (name, address, type; blank cells are skipped)</SelectItem></SelectContent></Select></div>
            </div>
            <div className="flex gap-2 justify-between flex-wrap">
              <Button variant="ghost" className="text-[hsl(var(--destructive))]" onClick={discard} data-testid="import-discard"><Trash2 className="w-4 h-4 mr-1" /> Discard upload</Button>
              <Button onClick={validate} disabled={validating || !(mapping.full_name || (mapping.first_name && mapping.last_name))} className="bg-[hsl(var(--gs-navy))]" data-testid="import-validate">{validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Check rows</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {batch && results.length > 0 && batch.status !== 'committed' && (
        <Card data-testid="import-review-card">
          <CardHeader>
            <CardTitle className="text-lg">3. Review rows</CardTitle>
            <div className="flex gap-2 flex-wrap pt-2" data-testid="import-summary">
              {['all', 'ready', 'warning', 'update', 'duplicate', 'error'].map(k => {
                const n = k === 'all' ? results.length : results.filter(r => r.status === k).length;
                return <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} className={filter === k ? 'bg-[hsl(var(--gs-navy))]' : ''} onClick={() => setFilter(k)} data-testid={`import-filter-${k}`}>{k === 'all' ? 'All' : STATUS[k].label} ({n})</Button>;
              })}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10"><Checkbox checked={importable.length > 0 && results.filter(r => ['ready', 'warning', 'update'].includes(r.status)).every(r => selected.has(r.row))} onCheckedChange={(c) => setSelected(c ? new Set(results.filter(r => ['ready', 'warning', 'update'].includes(r.status)).map(r => r.row)) : new Set())} aria-label="Select all" data-testid="import-select-all" /></TableHead>
                  <TableHead>Row</TableHead><TableHead>Status</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>DOB</TableHead><TableHead>Postcode</TableHead><TableHead>Type</TableHead><TableHead>Expiry</TableHead><TableHead>Notes</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {shown.map(r => {
                    const S = STATUS[r.status]; const can = ['ready', 'warning', 'update'].includes(r.status);
                    return (
                      <TableRow key={r.row} className={r.status === 'error' ? 'bg-red-50/40' : ''} data-testid={`import-row-${r.row}`}>
                        <TableCell><Checkbox checked={selected.has(r.row)} disabled={!can} onCheckedChange={(c) => { const s = new Set(selected); c ? s.add(r.row) : s.delete(r.row); setSelected(s); }} aria-label={`Select row ${r.row}`} data-testid={`import-row-select-${r.row}`} /></TableCell>
                        <TableCell className="text-xs">{r.row}</TableCell>
                        <TableCell><Badge className={`text-xs whitespace-nowrap ${S.cls}`} data-testid={`import-row-status-${r.row}`}><S.icon className="w-3 h-3 mr-1" />{S.label}</Badge></TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{r.normalized.full_name || <span className="text-red-700 italic">missing</span>}</TableCell>
                        <TableCell className="text-xs">{r.normalized.email}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.normalized.dob}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.normalized.postcode}</TableCell>
                        <TableCell className="text-xs capitalize">{r.normalized.membership_type}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.normalized.membership_type === 'lifetime' ? '—' : (r.normalized.expiry_date || 'rule applies')}</TableCell>
                        <TableCell className="text-xs max-w-[320px]">
                          {r.match && <div className="text-blue-800">Matches member {r.match.membership_number} ({r.match.full_name})</div>}
                          {r.issues.map((x, i) => <div key={stableRecordKey(x)} className="text-red-700">{x}</div>)}
                          {r.warnings.map((x, i) => <div key={stableRecordKey(x)} className="text-amber-800">{x}</div>)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-4 border-t">
              <p className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Info className="w-4 h-4" /> Rows marked <em>Error</em> or <em>Already a member</em> are never imported.</p>
              <Button onClick={() => setConfirmOpen(true)} disabled={importable.length === 0} className="bg-[hsl(var(--gs-navy))]" data-testid="import-open-confirm"><Users className="w-4 h-4 mr-2" /> Import {importable.length} selected</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {batch && batch.status === 'committed' && (
        <Card data-testid="import-result-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-700" /> Import complete</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-4 gap-3">
              {[['New members', result?.created ?? batch.committed?.created ?? 0, 'text-green-700'], ['Updated', result?.updated ?? batch.committed?.updated ?? 0, 'text-blue-700'], ['Skipped', result?.skipped ?? batch.committed?.skipped ?? 0, ''], ['Emails sent', 0, '']].map(([l, v, c]) => (
                <Card key={l}><CardContent className="p-4"><div className={`text-2xl font-bold ${c}`} data-testid={`import-result-${l.toLowerCase().replace(/ /g, '-')}`}>{v}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{l}</div></CardContent></Card>
              ))}
            </div>
            {(result?.created_list || []).length > 0 && (
              <div className="text-sm">
                <div className="font-medium mb-1">New membership numbers</div>
                <div className="max-h-56 overflow-y-auto rounded border p-2 font-mono text-xs space-y-0.5" data-testid="import-created-list">{result.created_list.map(c => <div key={c.membership_number}>{c.membership_number} — {c.full_name}</div>)}</div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard.writeText(result.created_list.map(c => `${c.membership_number}\t${c.full_name}`).join('\n')); toast.success('Copied'); }}><Copy className="w-3.5 h-3.5 mr-1" /> Copy list</Button>
              </div>
            )}
            {(result?.errors || batch.committed?.errors || []).length > 0 && <div className="text-sm text-red-700">{(result?.errors || batch.committed?.errors).map((e, i) => <div key={stableRecordKey(e)}>Row {e.row}: {e.error}</div>)}</div>}
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Welcome / update emails were <strong>not</strong> sent. Run a campaign from Members when the committee is ready.</p>
            <Button variant="ghost" size="sm" className="text-[hsl(var(--destructive))]" onClick={discard} data-testid="import-purge-rows"><Trash2 className="w-4 h-4 mr-1" /> Remove raw file data (keeps audit record)</Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setConfirmTick(false); }}>
        <DialogContent data-testid="import-confirm-dialog">
          <DialogHeader><DialogTitle>Import {importable.length} member{importable.length === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription>New records get an 18-character membership number and an active membership. Existing members marked “Update existing” have their contact details refreshed. No emails will be sent.</DialogDescription></DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={confirmTick} onCheckedChange={(c) => setConfirmTick(!!c)} data-testid="import-confirm-tick" /><span>I have reviewed the rows above and want to import them into the member database.</span></label>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button><Button onClick={commit} disabled={!confirmTick || committing} className="bg-[hsl(var(--gs-navy))]" data-testid="import-confirm-submit">{committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{committing ? 'Importing…' : 'Import now'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
